import { ErrorMessage, Field, Form, Formik, FormikHelpers } from 'formik';
import Image from 'next/image';
import React, { Dispatch, SetStateAction, useRef } from 'react';
import { styled } from 'styled-components';
import { RatingField } from '../../../shared-components/RatingGroupStars/RatingGroupStars';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import axios from 'axios';
import { Review } from '../../../pages/[slug]/[slug2]';
import client from '../../../apollo-client';
import { BOOKING_DATA } from '../../../api/fetch_user_details';
import { useMutation } from '@apollo/client';
import { INSERT_IMAGE_REVIEW } from '../../../api/reviews';
import { useState, useEffect } from 'react';
import router from 'next/router';
import Link from 'next/link';
import MediaUploader, {
  ImageWithPreview as MediaImageWithPreview
} from '../../../shared-components/MediaUploader/MediaUploader';
import { deleteFromS3 } from '../../../utils/s3';
interface IProps {
  setIsModalOpen: (open: boolean) => void;
  onClose: () => void;
  id: number | string | undefined;
  selectedReviewData: Review | null;
  setSelectedReviewData: React.Dispatch<React.SetStateAction<Review | null>>;
  fetchReviews: () => Promise<void>;
  tourName?: string;
  fetchReviewAggregate?: () => Promise<void>;
  fetchRatingCounts?: () => Promise<void>;
  setSelectedRating?: React.Dispatch<React.SetStateAction<string>>;
  setCurrentPage?: React.Dispatch<React.SetStateAction<number>>;
  setBookingId?: (bookingId: string) => void;
  initialData?: {
    name: string;
    email: string;
    rating: number;
    reviewTitle: string;
    reviewText: string;
    tourDate: string;
    productId: string;
    cityId: string;
    bookingId: string;
  };
}

interface Props {
  isChecked: boolean;
}
const CheckBoxTextStyle = styled.p<Props>`
  opacity: ${(props: any) => (props.isChecked ? '1' : '0.5')};
  width: 90%;
`;

const AddReviewModal = ({
  onClose,
  initialData,
  setIsModalOpen,
  id,
  tourName,
  selectedReviewData,
  setSelectedReviewData,
  fetchReviews,
  fetchReviewAggregate,
  fetchRatingCounts,
  setSelectedRating,
  setCurrentPage,
  setBookingId
}: IProps) => {
  const modalRef: any = useRef(null);
  const [images, setImages] = useState<MediaImageWithPreview[]>([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [allUploadsComplete, setAllUploadsComplete] = useState(true);

  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [insertReview] = useMutation(INSERT_IMAGE_REVIEW);

  const handleUploadStatusChange = (isUploading: boolean) => {
    setUploadingImages(isUploading);
  };

  useEffect(() => {
    const hasImages = images?.length > 0;
    const allUploaded = images.every(
      img => img?.uploadedUrl || img?.uploadError
    );
    const noneUploading = !images.some(img => img?.isUploading);

    setAllUploadsComplete(!hasImages || (allUploaded && noneUploading));
  }, [images]);
  // Get bookingId from localStorage
  const [bookingIdFromStorage, setBookingIdFromStorage] = useState<
    string | null
  >(null);
  // Safe localStorage access for tour name
  const [tourNameFromStorage, setTourNameFromStorage] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const tourData = localStorage.getItem('tour');
        if (tourData) {
          const parsedTourData = JSON.parse(tourData);
          const tourName = parsedTourData.tour;
          setTourNameFromStorage(tourName);
        }
      } catch (error) {
        console.error('Error parsing tour data from localStorage:', error);
        setTourNameFromStorage(null);
      }
    }
  }, []);
  useEffect(() => {
    const storedBookingId = localStorage.getItem('bookingId');
    if (storedBookingId) {
      setBookingIdFromStorage(storedBookingId);
    }
  }, []);

  const handlePostReviewSubmission = async () => {
    setTimeout(async () => {
      if (setSelectedRating) setSelectedRating('');
      if (setCurrentPage) setCurrentPage(1);
      if (fetchReviewAggregate) await fetchReviewAggregate();
      if (fetchRatingCounts) await fetchRatingCounts();
      const element = document.getElementById('firstdiv');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        element.focus({ preventScroll: true });
      }
    }, 1800);
  };

  const closeModal = async () => {
    if (!reviewSubmitting && images?.length > 0) {
      const uploadedFiles = images?.filter(img => img?.uploadedUrl);
      if (uploadedFiles?.length > 0) {
        for (const img of uploadedFiles) {
          if (img?.uploadedUrl) {
            await deleteFromS3(img?.uploadedUrl);
          }
        }
      }
    }
    onClose();
    setIsModalOpen(false);
    setSelectedReviewData(null);
  };

  const bookingId =
    initialData?.bookingId ||
    new URLSearchParams(window.location.search).get('id');

  const validationSchema = Yup.object().shape({
    name: Yup.string().when([], {
      is: () => !bookingId,
      then: schema =>
        schema
          .trim()
          .test(
            'no-blank-spaces',
            'Name cannot contain only blank spaces',
            value => {
              return !/^\s*$/.test(value || '');
            }
          )
          .min(2, 'Name must be at least 2 characters')
          .required('Name is required'),
      otherwise: schema => schema.optional()
    }),
    rating: Yup.number()
      .min(0.5, 'Please select a rating')
      .required('Please select a rating'),
    review: Yup.string()
      .trim()
      .test(
        'no-blank-spaces',
        'Review cannot contain only blank spaces',
        value => {
          return !/^\s*$/.test(value || '');
        }
      )
      .min(8, 'Minimum Length should be 8 characters!')
      .required('Review is required'),
    termsConditions: Yup.boolean()
      .oneOf([true], 'Please agree to the Terms & Conditions')
      .required('Please agree to the Terms & Conditions')
  });

  const handleSubmit = async (values: any) => {
    const uploadedImageObjects: string[] = images
      ?.filter(img => img?.uploadedUrl)
      ?.map(img => img?.uploadedUrl!);

    // Case 1: Pre-filled initial data (usually from booking)
    if (initialData) {
      try {
        const fullName = initialData?.name;
        const firstName = fullName?.split(' ')[0];
        const payload: any = {
          bookingId,
          productId: initialData?.productId,
          rating: values?.rating,
          review: values?.review,
          userName: firstName
        };

        if (uploadedImageObjects?.length > 0) {
          payload.images = uploadedImageObjects;
        }

        const headers = {
          'Content-Type': 'application/json'
        };

        const response = await axios.post(
          `${process.env.NEXT_PUBLIC_FIREBASE_WEBSITE_CONNECTION}/strapi-product-review`,
          payload,
          { headers, timeout: 10000 }
        );

        if (response?.data?.message === 'Review added successfully') {
          await handlePostReviewSubmission();
          toast.success('Your review has been submitted successfully!');
          // Clear bookingId from localStorage
          localStorage.removeItem('bookingId');
          // Clear bookingId from parent component state
          if (setBookingId) {
            setBookingId('');
          }
        }

        closeModal();
        return;
      } catch (error) {
        console.error('Error submitting review with initial data:', error);
        toast.error('An error occurred while submitting the review.');
        return;
      }
    }

    // Case 2: Guest submission with valid booking ID
    if (bookingId) {
      return handleGuestReview(values, bookingId, uploadedImageObjects);
    }

    // Case 3: Regular user submission
    return handleUserReview(values, uploadedImageObjects);
  };

  const handleGuestReview = async (
    values: any,
    bookingId: string,
    imageUrls: string[]
  ) => {
    try {
      const bookingResponse = await client.query({
        query: BOOKING_DATA,
        variables: { bookingId }
      });

      const booking = bookingResponse?.data?.boatnew_booking?.[0];
      const fullName = booking?.customer?.contact_name || 'Guest';
      const firstName = fullName?.split(' ')[0];
      const url = `${process.env.NEXT_PUBLIC_FIREBASE_WEBSITE_CONNECTION}/strapi-product-review`;
      const data: any = {
        productId: id?.toString(),
        rating: values?.rating,
        review: values?.review,
        bookingId,
        userName: firstName
      };

      if (imageUrls?.length > 0) {
        data.images = imageUrls;
      }

      const response = await axios?.post(url, data);

      await handlePostReviewSubmission();
      setIsModalOpen(false);
      toast.success(response?.data?.message);
      if (tourName === tourNameFromStorage) {
        // Clear bookingId from localStorage
        localStorage.removeItem('bookingId');
        // Clear bookingId from parent component state
        if (setBookingId) {
          setBookingId('');
        }
      }
    } catch (error) {
      console.error('Guest review submission error:', error);
      if (axios?.isAxiosError(error) && error?.response?.data?.error) {
        toast.error(
          "You've already reviewed this product Booking ID:" + bookingId
        );
      } else {
        toast.error('An error occurred while submitting the review');
      }
    }
  };

  const handleUserReview = async (
    values: any,
    uploadedImageObjects: string[]
  ) => {
    try {
      const now = new Date().toISOString();
      await insertReview({
        variables: {
          title: 'User Review',
          review: values?.review,
          traveller: values?.name,
          rating: values?.rating,
          date: now,
          source: 'website',
          product: parseInt(id?.toString() || 'not found'),
          published_at: now,
          created_at: now,
          updated_at: now,
          city: initialData?.cityId ? parseInt(initialData?.cityId) : null,
          image_urls: uploadedImageObjects || null,
          booking_id:
            tourName === tourNameFromStorage
              ? bookingIdFromStorage || null
              : null
        }
      });

      await handlePostReviewSubmission();
      setIsModalOpen(false);
      toast.success('Review submitted successfully');
      // Clear bookingId from localStorage
      if (tourName === tourNameFromStorage) {
        localStorage.removeItem('bookingId');
        if (setBookingId) {
          setBookingId('');
        }
      }
    } catch (error) {
      console.error('Review submission failed:', error);
      toast.error('Error submitting review');
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-[1150] bg-gray-800 bg-opacity-50 flex items-center justify-center">
        <div
          ref={modalRef}
          className="bg-white rounded-md h-[90%] xl:h-[80%]  w-[90%] md:w-[530px] lg:w-[600px] px-6 py-4 lg:px-6 lg:py-5 xl:px-12 xl:py-7 overflow-y-auto"
        >
          <div className="flex justify-between mb-4 xl:mb-8 items-center">
            <h2 className="text-sm md:text-lg">
              Share your experience with us
            </h2>
            <span className="cursor-pointer" onClick={closeModal}>
              <Image
                src={'/close-icon.svg'}
                alt="close-icon"
                width={20}
                height={20}
              />
            </span>
          </div>
          <div>
            <Formik
              initialValues={{
                name: selectedReviewData ? selectedReviewData?.name : '',
                review: selectedReviewData ? selectedReviewData?.review : '',
                rating: selectedReviewData ? selectedReviewData?.rating : 0,
                termsConditions: false
              }}
              validationSchema={validationSchema}
              onSubmit={async (values, formikHelpers) => {
                setReviewSubmitting(true);
                await handleSubmit(values);
                setReviewSubmitting(false);
                formikHelpers.setSubmitting(false);
              }}
            >
              {({ isSubmitting, setFieldValue, resetForm, values }) => (
                <Form>
                  <div
                    className={`flex flex-col gap-4 xl:gap-5 ${
                      reviewSubmitting ? 'pointer-events-none' : ''
                    }`}
                  >
                    {!bookingId && (
                      <div className="flex flex-col gap-2">
                        <label className="block text-[14px] xl:text-[16px] font-[500] text-[#4F4F4F]">
                          Name
                        </label>
                        <Field
                          id="name"
                          name="name"
                          type="text"
                          placeholder="Enter Name"
                          className="block focus:outline-none w-[100%] rounded-[10px] px-3 py-2 md:px-5 md:py-3 bg-[#EEEEEE] text-base xxsm:placeholder:text-[14px] md:placeholder:text-[16px]"
                        />
                        <ErrorMessage
                          name="name"
                          component="div"
                          className="text-red-400 xxsm:mt-0 md:mt-2 xxsm:text-[12px] md:text-[14px]"
                        />
                      </div>
                    )}

                    <div className="flex flex-col gap-2">
                      <label className="block text-[14px] xl:text-[16px] font-[500] text-[#4F4F4F]">
                        Rating
                      </label>
                      <Field
                        name="rating"
                        component={RatingField}
                        setFieldValue={setFieldValue}
                      />
                      <ErrorMessage
                        name="rating"
                        component="div"
                        className="text-red-400 xxsm:mt-0 md:mt-2 xxsm:text-[12px] md:text-[14px]"
                      />
                    </div>

                    <div>
                      <Field
                        id="review"
                        name="review"
                        as="textarea"
                        placeholder="Write Your Review"
                        className="block focus:outline-none w-[100%] rounded-[10px] px-3 py-2 md:px-5 md:py-4 bg-[#EEEEEE] md:h-[120px] lg:h-[140px] xl:h-[150px] min-h-[100px] text-base xxsm:placeholder:text-[14px] md:placeholder:text-[16px]"
                      />
                      <ErrorMessage
                        name="review"
                        component="div"
                        className="text-red-400 xxsm:mt-0 md:mt-2 xxsm:text-[12px] md:text-[14px]"
                      />
                    </div>
                    <div>
                      <label className="block text-[14px] xl:text-[16px] font-[500] text-[#4F4F4F]">
                        Upload Media{' '}
                        <span className="text-xs text-gray-500">
                          (Optional)
                        </span>
                      </label>
                      <MediaUploader
                        images={images}
                        setImages={setImages}
                        showTermsText={false}
                        onUploadStatusChange={handleUploadStatusChange}
                      />
                    </div>

                    <div className="flex items-start gap-3 pt-0 md:pt-3 flex-col">
                      <div className="flex items-start gap-3 pt-3">
                        <Field
                          type="checkbox"
                          name="termsConditions"
                          checked={values?.termsConditions}
                          onChange={(e: any) => {
                            setFieldValue('termsConditions', e.target.checked);
                          }}
                          className="mt-1 cursor-pointer"
                        />
                        <CheckBoxTextStyle
                          isChecked={values?.termsConditions}
                          className="xxsm:text-sm text-justify md:text-sm font-[500] text-[#4F4F4F]"
                        >
                          I certify that this review is based on my experience
                          and is my genuine opinion, and have not been offered
                          any incentive or payment originating from the
                          establishment to rewrite this review.
                          {images?.length > 0 && (
                            <p className="mt-2">
                              By uploading this media, I confirm that I own the
                              content, give See Sight Tours permission to use
                              the media, and agree to the{' '}
                              <Link
                                href={{
                                  pathname: '/terms-and-conditions',
                                  query: {
                                    ref: router.asPath,
                                    fromImageUpload: 'true'
                                  }
                                }}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline text-blue-600 hover:text-blue-800 visited:text-purple-600"
                              >
                                terms & conditions
                              </Link>
                              .
                            </p>
                          )}
                        </CheckBoxTextStyle>
                      </div>

                      <ErrorMessage
                        name="termsConditions"
                        component="div"
                        className="text-red-400 xxsm:text-[14px] md:text-base"
                      />
                    </div>

                    <div className="flex justify-end gap-3">
                      <button
                        className={`py-2 px-10 w-fit flex justify-center items-center focus:outline-none text-[14px] sm:text-base xxsm:font-normal md:font-semibold float-right rounded-[10px] xxsm:mt-2 xl:mt-5 ${
                          isSubmitting || !allUploadsComplete
                            ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                            : 'bg-[#1F2C5B] text-white'
                        }`}
                        type="submit"
                        disabled={isSubmitting || !allUploadsComplete}
                      >
                        {isSubmitting
                          ? 'Submitting...'
                          : uploadingImages
                          ? 'Uploading files...'
                          : !allUploadsComplete
                          ? 'Uploading files...'
                          : 'Submit'}
                      </button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddReviewModal;
